import type {
  BillingDocumentType,
  BillingProcess,
} from '@policyvision/contracts';
import type { ParseKeys } from 'i18next';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Badge } from '@/common/components/ui/badge';
import { Button } from '@/common/components/ui/button';
import { Form, FormField } from '@/common/components/ui/form';
import { cn } from '@/lib/utils';

import type { BillingSummary } from '../api/FetchBillingList';
import { getNamingToken } from '../types/NamingToken';

type NamingRuleFormProps = {
  billing: BillingSummary;
  process: BillingProcess;
  documentType?: BillingDocumentType;
  onCancel: () => void;
};

export const NamingRuleForm = ({
  billing,
  process,
  documentType,
  onCancel,
}: NamingRuleFormProps) => {
  const { t } = useTranslation();

  const splitNamingRule = (documentType?.namingRule ?? '')
    .split(/\{(\w+)\}/)
    .map((part, index) => ({ part, isToken: index % 2 === 1 }));

  const tokenKeys = splitNamingRule
    .filter(({ isToken }) => isToken)
    .map(({ part }) => part);

  const schema = z.object(
    Object.fromEntries(
      tokenKeys.map((tokenKey) => [tokenKey, getNamingToken(tokenKey).schema]),
    ),
  );

  const form = useForm({
    defaultValues: Object.fromEntries(
      tokenKeys.map((tokenKey) => [
        tokenKey,
        getNamingToken(tokenKey).defaultValue?.(billing, process) ?? '',
      ]),
    ),
  });

  const values = useWatch({ control: form.control });
  const { data, error } = schema.safeParse(values);
  const invalidTokens = new Set(error?.issues.map((issue) => issue.path[0]));

  const generatedName =
    data &&
    splitNamingRule
      .map(({ part, isToken }) => (isToken ? data[part] : part))
      .join('');

  return (
    <Form {...form}>
      {documentType && (
        <>
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-muted-foreground">
              {t('billing.drawer.rename.newName')}
            </span>
            <div className="flex flex-wrap items-center gap-y-1 rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground focus-within:border-primary">
              {splitNamingRule.map(({ part, isToken }, index) =>
                isToken ? (
                  <FormField
                    key={`${part}-${index}`}
                    control={form.control}
                    name={part}
                    render={({ field }) => {
                      const { label } = getNamingToken(part);
                      const isInvalid = invalidTokens.has(part);

                      return (
                        <input
                          {...field}
                          aria-label={label ? t(label) : part}
                          aria-invalid={isInvalid}
                          placeholder={label ? t(label) : part}
                          className={cn(
                            'rounded bg-transparent outline-none field-sizing-content',
                            isInvalid
                              ? 'border border-destructive bg-destructive/10 px-1'
                              : 'border border-primary px-1 text-foreground',
                          )}
                        />
                      );
                    }}
                  />
                ) : (
                  <span key={`${part}-${index}`} className="whitespace-pre">
                    {part}
                  </span>
                ),
              )}
            </div>

            {generatedName ? (
              <p className="text-xs text-muted-foreground">
                {t('billing.drawer.rename.generatedName')}: {generatedName}
              </p>
            ) : (
              [...new Set(error?.issues.map((item) => item.message))].map(
                (message) => (
                  <p key={message} className="text-xs text-destructive">
                    {t(message as ParseKeys)}
                  </p>
                ),
              )
            )}
          </div>

          <dl className="flex flex-col gap-1 text-sm">
            <div className="flex items-center justify-between">
              <dt className="text-muted-foreground">
                {t('billing.drawer.rename.folder')}
              </dt>
              <dd>
                <Badge variant="outline" className="rounded-full">
                  {documentType.folder}
                </Badge>
              </dd>
            </div>

            {documentType.subfolder && (
              <div className="flex items-center justify-between">
                <dt className="text-muted-foreground">
                  {t('billing.drawer.rename.subfolder')}
                </dt>
                <dd>
                  <Badge variant="outline" className="rounded-full">
                    {documentType.subfolder}
                  </Badge>
                </dd>
              </div>
            )}
          </dl>
        </>
      )}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button type="button" disabled={!generatedName}>
          {t('billing.drawer.rename.action')}
        </Button>
      </div>
    </Form>
  );
};
